import { Alert, Box, useTheme } from "@mui/material";
import * as React from "react";

import { SearchResults } from "@/Search/SearchResults";
import { useDeviceDatasets } from "@/shared/domain/devices/hooks/useDeviceDatasets";

import { useDeviceContext } from "../Device/useDeviceContext";

/**
 * Datasets tab of the device detail view.
 *
 * Triggered when the user navigates to `/devices/:deviceId/datasets`.
 * Submits a structured query for datasets whose `device_id` equals the
 * current device (scoped to the active organization) and renders the
 * resulting list with `<SearchResults />`. Reads `deviceId` and `org`
 * from `<Device />`'s outlet context rather than re-deriving them from
 * the URL or auth state, so this component renders correctly only when
 * mounted as a nested route under `<Device />`.
 *
 * Failure-mode posture:
 * 1. If the query submission fails, render an inline error alert in
 *    place of the search results grid.
 * 2. Per-row errors are surfaced through `lastError` / `onError` and
 *    handled inside `<SearchResults />`.
 */
export function DeviceDatasets() {
  const theme = useTheme();
  const { deviceId, org } = useDeviceContext();
  const [selectedDatasets, setSelectedDatasets] = React.useState<Set<string>>(
    new Set(),
  );
  const [lastError, setLastError] = React.useState<Error | null>(null);

  const { data: queryId, error } = useDeviceDatasets(deviceId, org?.org_id);

  if (error) {
    return (
      <Box mt={theme.spacing(1)} mb={theme.spacing(2)}>
        <Alert severity="error">
          Failed to load datasets for device {deviceId}: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box mt={theme.spacing(-1)} mb={theme.spacing(2)}>
      <SearchResults
        queryId={queryId}
        lastError={lastError}
        onError={setLastError}
        selectedDatasets={selectedDatasets}
        setSelectedDatasets={setSelectedDatasets}
      />
    </Box>
  );
}
